import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Alert, Snackbar, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { testBackendConnection } from './utils/testConnection';

// Layout components
import Layout from './components/Layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import SimpleLogin from './pages/SimpleLogin';
import Closet from './pages/Closet';
import AddItem from './pages/AddItem';
import Chat from './pages/Chat';
import StyleProfile from './pages/StyleProfile';
import OutfitHistory from './pages/OutfitHistory';
import SocialFeed from './pages/SocialFeed';
import UserProfile from './pages/UserProfile';
import FollowersList from './pages/FollowersList';
import FollowingList from './pages/FollowingList';
import ScanCloset from './pages/ScanCloset';
import Legal from './pages/Legal';
import { sanitizeReturnTo } from './utils/authNavigation';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, authNotice } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <Box className="route-loading-app">
        <Box component="header" className="route-loading-topbar">
          <Box className="route-loading-brand">FM</Box>
          <Typography fontWeight={800}>FashionMate AI</Typography>
        </Box>
        <Box className="route-loading-frame">
          <Box component="aside" className="route-loading-nav" aria-hidden="true">
            {['Home', 'My Closet', 'AI Stylist', 'Style Profile', 'Outfit History'].map((label) => (
              <Box className="route-loading-nav-item" key={label}>{label}</Box>
            ))}
          </Box>
          <Box component="main" className="route-loading-main">
            <Box className="route-loading-card">
              <CircularProgress size={30} />
              <Typography variant="h6">Loading your styling workspace</Typography>
              <Typography variant="body2" color="text.secondary">Checking your session and closet data.</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    const returnTo = sanitizeReturnTo(`${location.pathname}${location.search}`);
    if (returnTo !== '/login') sessionStorage.setItem('fashionmate_return_to', returnTo);
    const reason = authNotice ? 'session-expired' : 'sign-in-required';
    return <Navigate to={`/login?reason=${reason}`} replace state={{ from: location }} />;
  }
  
  return children;
};

// Main App with routes
const AppRoutes = () => {
  const [connectionError, setConnectionError] = useState(null);
  const location = useLocation();

  // Test backend connection on component mount
  useEffect(() => {
    // Skip connection test on auth callback to avoid confusing errors during auth flow
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/auth/callback' || params.get('auth_callback') === 'true') {
      return;
    }
    
    const checkBackendConnection = async () => {
      const result = await testBackendConnection();
      if (!result.success) {
        setConnectionError(result.details);
      }
    };

    checkBackendConnection();
  }, [location.pathname, location.search]);

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/simple-login" element={<SimpleLogin />} />
        <Route path="/auth/callback" element={<Login />} />
        <Route path="/terms" element={<Legal type="terms" />} />
        <Route path="/privacy" element={<Legal type="privacy" />} />
        <Route path="/" element={
          <ProtectedRoute>
            <Layout>
              <Home />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/closet" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary componentName="Closet">
                <Closet />
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/closet/add" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary componentName="Add Item">
                <AddItem />
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/closet/scan" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary componentName="Scan Closet">
                <ScanCloset />
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary componentName="Chat">
                <Chat />
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/style-profile" element={
          <ProtectedRoute>
            <Layout>
              <StyleProfile />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/outfit-history" element={
          <ProtectedRoute>
            <Layout>
              <OutfitHistory />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/social" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary componentName="Social Feed">
                <SocialFeed />
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/profile/:userId" element={
          <ProtectedRoute>
            <Layout>
              <UserProfile />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/users/:userId/followers" element={
          <ProtectedRoute>
            <Layout>
              <FollowersList />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/users/:userId/following" element={
          <ProtectedRoute>
            <Layout>
              <FollowingList />
            </Layout>
          </ProtectedRoute>
        } />
        {/* Redirect all other routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Connection error alert */}
      <Snackbar 
        open={!!connectionError} 
        autoHideDuration={10000} 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled">
          Backend Connection Error: {connectionError}
        </Alert>
      </Snackbar>
    </>
  );
};

// Wrap the app with the AuthProvider
const App = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App; 
